import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { vars } from '../../../theme';
import { motion } from 'framer-motion';
import { Close16 } from '@honeybook/icons';
import {
    IMemberToastAction,
    IMemberToastProps,
    IToastAction
} from '../HBToast.types';
import HBText from '../../HBText/HBText';
import HBMediaElement from '../../HBMediaElement/HBMediaElement';
import { mediumUp } from '../../../styles/breakpoints';
import { flexCenterAll, flexColumn } from '../../../styles/mixins';
import ToastActions from '../ToastActions';
import ToastActionLink from '../ToastActionLink';
import { Z_INDEX } from '../../../styles/variables';
import { HBButtonCustomTheme } from '../../HBButton/HBButton.types';
import { Button } from '../../HBButton/HBButton.styles';

const LOGO_SIZE = 54;
const ANIMATION_DURATION_IN_SEC = 0.5;
const REMOVE_TIMEOUT_IN_MS = 400;
export const MEMBER_TOAST_CLOSE_BUTTON_TEST_ID =
    'hb-member-toast-close--button';

interface Props extends IMemberToastProps {
    isFirstInQueue: boolean;
    buttonTheme: HBButtonCustomTheme;
}

const MemberToast = ({
    actionLink,
    actions,
    autoDismissMs,
    buttonTheme,
    iconUrl,
    id,
    isDismissible,
    isFirstInQueue,
    onDismiss,
    removeItem,
    text,
    title
}: Props) => {
    const [isDismissed, setIsDismissed] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const isShown = isFirstInQueue && !isDismissed;

    useEffect(() => {
        if (autoDismissMs && isShown && !isHovered) {
            const timeoutId = setTimeout(() => {
                setIsDismissed(true);
            }, autoDismissMs);
            return () => clearTimeout(timeoutId);
        }
    }, [autoDismissMs, isShown, isHovered]);

    useEffect(() => {
        if (!isShown && isDismissed) {
            setTimeout(() => removeItem(id), REMOVE_TIMEOUT_IN_MS);
        }
    }, [id, isDismissed, isShown, removeItem]);

    const handleDismiss = () => {
        setIsDismissed(true);
        onDismiss?.();
    };

    const onActionClick = (action: IMemberToastAction) => {
        action?.onClick();
        handleDismiss();
    };

    // use secondary button if 2 cta’s are needed
    // use only secondary cta as a stand alone button if the action is optional and the toast is dismissable
    const themedActions: IToastAction[] = useMemo(() => {
        return actions?.map((action, idx) => {
            const isSecondaryButton =
                (idx === 0 && actions.length > 1) ||
                (actions.length === 1 && isDismissible);
            const secondaryTheme: HBButtonCustomTheme = {
                ...buttonTheme,
                background: 'transparent',
                color: vars.colors.text.primary,
                hoverBackgroundColor: vars.colors.surface.itemHover
            };
            return {
                ...action,
                size: 'xs',
                customTheme: isSecondaryButton ? secondaryTheme : buttonTheme
            };
        });
    }, [actions, buttonTheme, isDismissible]);

    return (
        <MemberToastContainer
            key={id}
            transition={{
                duration: ANIMATION_DURATION_IN_SEC,
                ease: 'easeInOut'
            }}
            initial={{ opacity: 0, y: 200 }}
            animate={isShown ? { opacity: 1, y: 0 } : { opacity: 0, y: 200 }}
            exit={{ opacity: 0, y: 200 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isDismissible && (
                <CloseIcon
                    data-testid={MEMBER_TOAST_CLOSE_BUTTON_TEST_ID}
                    color={vars.colors.text.secondary}
                    onClick={handleDismiss}
                />
            )}

            <LogoAndTextContainer>
                {iconUrl && (
                    <CompanyLogo>
                        <HBMediaElement
                            width={LOGO_SIZE}
                            height={LOGO_SIZE}
                            path={iconUrl}
                            crop="fit"
                        />
                    </CompanyLogo>
                )}

                <TextContainer>
                    {title && <Title>{title}</Title>}

                    <Text>{text}</Text>
                </TextContainer>
            </LogoAndTextContainer>

            {actions && (
                <ActionContainer>
                    <ToastActions
                        actions={themedActions}
                        onActionClick={onActionClick}
                    />
                </ActionContainer>
            )}
            {actionLink && (
                <ToastActionLink
                    actionLink={actionLink}
                    onActionClick={onActionClick}
                />
            )}
        </MemberToastContainer>
    );
};

export default MemberToast;

// -------------------------------- Styles -------------------------------- //
const MemberToastContainer = styled(motion.div)`
    // Positioning:
    position: fixed;
    z-index: ${Z_INDEX.TOAST};
    left: 8px;
    bottom: 30px;
    right: 8px;
    margin-left: auto;
    ${mediumUp} {
        left: unset;
        bottom: 80px;
        right: 30px;
    }

    // Design:
    box-sizing: border-box;
    padding: 20px;
    background-color: ${vars.colors.surface.container};
    border-radius: ${vars.radius.md};
    box-shadow: ${vars.shadow['40']};
    ${Button} {
        padding-left: 12px;
        padding-right: 12px;
    }

    // Sizing:
    max-width: 420px;
    ${flexColumn};
    ${mediumUp} {
        min-width: 420px;
        max-width: 520px;
    }
`;

const CloseIcon = styled(Close16)`
    position: absolute;
    top: 14px;
    right: 14px;
`;

const CompanyLogo = styled.div`
    min-width: ${LOGO_SIZE}px;
    height: ${LOGO_SIZE}px;
    ${flexCenterAll};
    border-radius: ${vars.radius.full};
    border: 1px solid ${vars.colors.stroke.secondary};
    margin-right: 12px;
    overflow: hidden;
`;

const LogoAndTextContainer = styled.div`
    display: flex;
    align-items: top;
`;

const TextContainer = styled.div`
    ${flexColumn}
    margin-top: 4px;
    margin-right: 20px;
`;

const Title = styled(HBText).attrs({ type: 'title14', color: 'text.primary' })`
    display: block;
    margin-bottom: 4px;
`;

const Text = styled(HBText).attrs({ type: 'body14', color: 'text.primary' })`
    display: block;
    white-space: pre-line;
`;

const ActionContainer = styled.div`
    margin-top: 12px;
    display: flex;
    justify-content: flex-end;
    button {
        margin-bottom: 0;
    }
`;
